import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import "./ReactQuillStyles.css"; // Custom styles for ReactQuill
import {
  Image,
  Input,
  Textarea,
  Button,
  Select,
  SelectItem,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Progress,
} from "@nextui-org/react";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import arrowBack from "../../Assets/blog/arrow-back.svg";
import { AuthContext } from "../../Context/AuthContext";
import {
  FiEdit2,
  FiArrowLeft,
  FiCheckCircle,
  FiXCircle,
  FiTag,
  FiUpload,
  FiSave,
  FiImage,
  FiX,
  FiPlus,
} from "react-icons/fi";
import { API_BASE_URL } from "../../config/apiConfig";

const API_BASE =  API_BASE_URL;

// Blog Service for Admin operations
const blogAdminService = {
  getBlog: (id, token) => axios.get(`${API_BASE}/admin/blogs/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  
  createBlog: (blogData, token) => axios.post(
    `${API_BASE}/admin/blogs`,
    blogData,
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  updateBlog: (id, blogData, token) => axios.put(
    `${API_BASE}/admin/blogs/${id}`,
    blogData,
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  publishBlog: (id, token) => axios.post(
    `${API_BASE}/admin/blogs/${id}/publish`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  deleteBlog: (id, token) => axios.delete(
    `${API_BASE}/admin/blogs/${id}`,
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  uploadImage: (formData, token) => axios.post(
    `${API_BASE}/admin/blogs/upload-image`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    }
  ),
  getDraftBlogs: (token) => axios.get(
    `${API_BASE}/admin/blogs/status/draft`,
    { headers: { Authorization: `Bearer ${token}` } }
  ),
};

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'font': [] }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    [{ 'align': [] }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'direction': 'rtl' }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'list': 'check' }],
    ['link'],
    ['blockquote', 'code-block'],
    [{ 'script': 'sub'}, { 'script': 'super' }],
    ['clean'],
    [{ 'separator': '---' }],
  ],
};

const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'bullet', 'ordered', 'indent',
  'direction',
  'link',
  'blockquote', 'code-block',
  'align',
  'clean'
];

// Updated Categories with Subcategories for ALL categories
const categories = [
  {
    value: "Travel Tips",
    label: "Travel Tips",
    hexColor: "#4F46E5",
    subcategories: [
      { value: "travel-tips-packing", label: "Packing Tips" },
      { value: "travel-tips-budget", label: "Budget Travel Tips" },
      { value: "travel-tips-safety", label: "Safety Tips" },
      { value: "travel-tips-planning", label: "Trip Planning" },
      { value: "travel-tips-accommodation", label: "Accommodation Tips" },
      { value: "travel-tips-transportation", label: "Transportation Tips" },
      { value: "travel-tips-food", label: "Food & Dining Tips" },
    ]
  },
  {
    value: "Travel",
    label: "Travel",
    hexColor: "#0EA5E9",
    subcategories: [
      { value: "travel-adventure", label: "Adventure Travel" },
      { value: "travel-budget", label: "Budget Travel" },
      { value: "travel-luxury", label: "Luxury Travel" },
      { value: "travel-solo", label: "Solo Travel" },
      { value: "travel-family", label: "Family Travel" },
      { value: "travel-backpacking", label: "Backpacking" },
      { value: "travel-road-trip", label: "Road Trips" },
      { value: "travel-beach", label: "Beach Destinations" },
      { value: "travel-mountain", label: "Mountain Getaways" },
      { value: "travel-cultural", label: "Cultural Immersion" },
      { value: "travel-food", label: "Food & Culinary Travel" },
      { value: "travel-wellness", label: "Wellness Retreats" },
    ]
  },
  {
    value: "Projects",
    label: "Projects",
    hexColor: "#10B981",
    subcategories: [
      { value: "projects-diy", label: "DIY Projects" },
      { value: "projects-home-improvement", label: "Home Improvement" },
      { value: "projects-gardening", label: "Gardening Projects" },
      { value: "projects-crafts", label: "Crafts & Art Projects" },
      { value: "projects-tech", label: "Tech Projects" },
      { value: "projects-woodworking", label: "Woodworking" },
      { value: "projects-renovation", label: "Home Renovation" },
      { value: "projects-upcycling", label: "Upcycling Projects" },
    ]
  },
  {
    value: "Event",
    label: "Event",
    hexColor: "#F59E0B",
    subcategories: [
      { value: "event-weddings", label: "Weddings" },
      { value: "event-birthdays", label: "Birthdays" },
      { value: "event-corporate", label: "Corporate" },
      { value: "event-conferences", label: "Conferences" },
      { value: "event-festivals", label: "Festivals" },
      { value: "event-concerts", label: "Concerts" },
      { value: "event-sports", label: "Sports" },
      { value: "event-charity", label: "Charity" },
      { value: "event-exhibitions", label: "Exhibitions" },
      { value: "event-launches", label: "Product Launches" },
      { value: "event-fashion", label: "Fashion" },
      { value: "event-music-nightlife", label: "Music & Nightlife" },
      { value: "event-family-lifestyle", label: "Family & Lifestyle" },
      { value: "event-arts-culture", label: "Arts & Culture" },
    ]
  },
  {
    value: "Sport",
    label: "Sport",
    hexColor: "#EF4444",
    subcategories: [
      { value: "sport-football", label: "Football" },
      { value: "sport-basketball", label: "Basketball" },
      { value: "sport-tennis", label: "Tennis" },
      { value: "sport-golf", label: "Golf" },
      { value: "sport-cricket", label: "Cricket" },
      { value: "sport-rugby", label: "Rugby" },
      { value: "sport-swimming", label: "Swimming" },
      { value: "sport-running", label: "Running" },
      { value: "sport-cycling", label: "Cycling" },
      { value: "sport-yoga", label: "Yoga" },
      { value: "sport-gym", label: "Gym & Fitness" },
      { value: "sport-extreme", label: "Extreme Sports" },
      { value: "sport-esports", label: "eSports" },
    ]
  },
  {
    value: "Wellness",
    label: "Wellness",
    hexColor: "#06B6D4",
    subcategories: [
      { value: "wellness-meditation", label: "Meditation" },
      { value: "wellness-yoga", label: "Yoga" },
      { value: "wellness-nutrition", label: "Nutrition" },
      { value: "wellness-fitness", label: "Fitness" },
      { value: "wellness-mental-health", label: "Mental Health" },
      { value: "wellness-sleep", label: "Sleep & Recovery" },
      { value: "wellness-holistic", label: "Holistic Health" },
      { value: "wellness-spa", label: "Spa & Relaxation" },
      { value: "wellness-ayurveda", label: "Ayurveda" },
      { value: "wellness-acupuncture", label: "Acupuncture" },
      { value: "wellness-massage", label: "Massage Therapy" },
      { value: "wellness-diet", label: "Diet & Detox" },
      { value: "wellness-mindfulness", label: "Mindfulness" },
    ]
  },
  {
    value: "Lifestyle",
    label: "Lifestyle",
    hexColor: "#14B8A6",
    subcategories: [
      { value: "lifestyle-fashion", label: "Fashion & Style" },
      { value: "lifestyle-beauty", label: "Beauty" },
      { value: "lifestyle-home", label: "Home & Decor" },
      { value: "lifestyle-relationships", label: "Relationships" },
      { value: "lifestyle-parenting", label: "Parenting" },
      { value: "lifestyle-pets", label: "Pets" },
      { value: "lifestyle-minimalism", label: "Minimalism" },
      { value: "lifestyle-sustainable", label: "Sustainable Living" },
      { value: "lifestyle-hobbies", label: "Hobbies" },
      { value: "lifestyle-entertainment", label: "Entertainment" },
      { value: "lifestyle-social", label: "Social Life" },
      { value: "lifestyle-personal-growth", label: "Personal Growth" },
    ]
  },
  {
    value: "Productivity",
    label: "Productivity",
    hexColor: "#F97316",
    subcategories: [
      { value: "productivity-time-management", label: "Time Management" },
      { value: "productivity-goal-setting", label: "Goal Setting" },
      { value: "productivity-habits", label: "Habit Building" },
      { value: "productivity-focus", label: "Focus & Concentration" },
      { value: "productivity-tools", label: "Productivity Tools" },
      { value: "productivity-work-life", label: "Work-Life Balance" },
      { value: "productivity-organization", label: "Organization" },
      { value: "productivity-planning", label: "Planning Systems" },
      { value: "productivity-motivation", label: "Motivation" },
      { value: "productivity-delegation", label: "Delegation" },
      { value: "productivity-remote-work", label: "Remote Work" },
      { value: "productivity-learning", label: "Learning Efficiency" },
    ]
  },
  {
    value: "Seasonal",
    label: "Seasonal",
    hexColor: "#D946EF",
    subcategories: [
      { value: "seasonal-christmas", label: "Christmas" },
      { value: "seasonal-halloween", label: "Halloween" },
      { value: "seasonal-easter", label: "Easter" },
      { value: "seasonal-new-year", label: "New Year" },
      { value: "seasonal-valentines", label: "Valentine's Day" },
      { value: "seasonal-thanksgiving", label: "Thanksgiving" },
      { value: "seasonal-summer", label: "Summer Activities" },
      { value: "seasonal-winter", label: "Winter Activities" },
      { value: "seasonal-spring", label: "Spring Activities" },
      { value: "seasonal-autumn", label: "Autumn Activities" },
      { value: "seasonal-holidays", label: "Holiday Planning" },
      { value: "seasonal-festivals", label: "Seasonal Festivals" },
    ]
  },
  {
    value: "Stories",
    label: "Stories",
    hexColor: "#8B5CF6",
    subcategories: [
      { value: "stories-personal", label: "Personal Stories" },
      { value: "stories-travel", label: "Travel Stories" },
      { value: "stories-fiction", label: "Fiction" },
      { value: "stories-inspirational", label: "Inspirational Stories" },
      { value: "stories-humorous", label: "Humorous Stories" },
      { value: "stories-mystery", label: "Mystery" },
      { value: "stories-romance", label: "Romance" },
      { value: "stories-adventure", label: "Adventure Stories" },
      { value: "stories-historical", label: "Historical Stories" },
      { value: "stories-fantasy", label: "Fantasy" },
      { value: "stories-sci-fi", label: "Science Fiction" },
      { value: "stories-interviews", label: "Interviews" },
    ]
  },
  {
    value: "Technology",
    label: "Technology",
    hexColor: "#3B82F6",
    subcategories: [
      { value: "tech-ai", label: "Artificial Intelligence" },
      { value: "tech-web", label: "Web Development" },
      { value: "tech-mobile", label: "Mobile Development" },
      { value: "tech-gadgets", label: "Gadgets & Reviews" },
      { value: "tech-security", label: "Cybersecurity" },
      { value: "tech-software", label: "Software Engineering" },
      { value: "tech-blockchain", label: "Blockchain & Crypto" },
      { value: "tech-data-science", label: "Data Science" },
      { value: "tech-cloud", label: "Cloud Computing" },
      { value: "tech-iot", label: "Internet of Things" },
      { value: "tech-gaming", label: "Gaming" },
      { value: "tech-startups", label: "Startups & Innovation" },
      { value: "tech-programming", label: "Programming Languages" },
      { value: "tech-hardware", label: "Hardware" },
      { value: "tech-robotics", label: "Robotics" },
      { value: "tech-vr-ar", label: "VR & AR" },
    ]
  },
  {
    value: "Food",
    label: "Food",
    hexColor: "#10B981",
    subcategories: [
      { value: "food-recipes", label: "Recipes" },
      { value: "food-restaurants", label: "Restaurant Reviews" },
      { value: "food-healthy", label: "Healthy Eating" },
      { value: "food-baking", label: "Baking" },
      { value: "food-vegan", label: "Vegan & Vegetarian" },
      { value: "food-desserts", label: "Desserts" },
      { value: "food-drinks", label: "Drinks & Beverages" },
      { value: "food-cooking-tips", label: "Cooking Tips" },
      { value: "food-meal-prep", label: "Meal Prep" },
      { value: "food-international", label: "International Cuisine" },
      { value: "food-street-food", label: "Street Food" },
      { value: "food-fine-dining", label: "Fine Dining" },
      { value: "food-food-travel", label: "Food Travel" },
    ]
  },
  {
    value: "Business",
    label: "Business",
    hexColor: "#0EA5E9",
    subcategories: [
      { value: "business-entrepreneurship", label: "Entrepreneurship" },
      { value: "business-marketing", label: "Marketing" },
      { value: "business-finance", label: "Finance" },
      { value: "business-management", label: "Management" },
      { value: "business-leadership", label: "Leadership" },
      { value: "business-sales", label: "Sales" },
      { value: "business-investing", label: "Investing" },
      { value: "business-strategy", label: "Business Strategy" },
      { value: "business-startups", label: "Startups" },
      { value: "business-ecommerce", label: "E-commerce" },
    ]
  },
  {
    value: "Education",
    label: "Education",
    hexColor: "#8B5CF6",
    subcategories: [
      { value: "education-learning", label: "Learning Techniques" },
      { value: "education-online", label: "Online Education" },
      { value: "education-career", label: "Career Development" },
      { value: "education-languages", label: "Language Learning" },
      { value: "education-skill-development", label: "Skill Development" },
      { value: "education-study-tips", label: "Study Tips" },
      { value: "education-parenting", label: "Education & Parenting" },
    ]
  },
  {
    value: "Finance",
    label: "Finance",
    hexColor: "#10B981",
    subcategories: [
      { value: "finance-investing", label: "Investing" },
      { value: "finance-saving", label: "Saving Money" },
      { value: "finance-budgeting", label: "Budgeting" },
      { value: "finance-stocks", label: "Stock Market" },
      { value: "finance-crypto", label: "Cryptocurrency" },
      { value: "finance-real-estate", label: "Real Estate" },
      { value: "finance-taxes", label: "Tax Planning" },
    ]
  },
  {
    value: "Health",
    label: "Health",
    hexColor: "#EF4444",
    subcategories: [
      { value: "health-fitness", label: "Fitness" },
      { value: "health-nutrition", label: "Nutrition" },
      { value: "health-mental", label: "Mental Health" },
      { value: "health-chronic", label: "Chronic Conditions" },
      { value: "health-preventive", label: "Preventive Care" },
      { value: "health-alternative", label: "Alternative Medicine" },
    ]
  },
  {
    value: "Photography",
    label: "Photography",
    hexColor: "#8B5CF6",
    subcategories: [
      { value: "photography-portrait", label: "Portrait Photography" },
      { value: "photography-landscape", label: "Landscape Photography" },
      { value: "photography-street", label: "Street Photography" },
      { value: "photography-wildlife", label: "Wildlife Photography" },
      { value: "photography-editing", label: "Photo Editing" },
      { value: "photography-equipment", label: "Camera Equipment" },
    ]
  },
  {
    value: "Books",
    label: "Books",
    hexColor: "#F59E0B",
    subcategories: [
      { value: "books-fiction", label: "Fiction" },
      { value: "books-nonfiction", label: "Non-Fiction" },
      { value: "books-self-help", label: "Self-Help" },
      { value: "books-reviews", label: "Book Reviews" },
      { value: "books-recommendations", label: "Recommendations" },
      { value: "books-writing", label: "Writing Tips" },
    ]
  },
];

const getCategoryHexColor = (category) => {
  const found = categories.find(cat => cat.value === category);
  return found?.hexColor || "#6B7280";
};

// Helper function to clean ReactQuill HTML
const cleanReactQuillHTML = (html) => {
  if (!html) return '';
  
  let cleaned = html
    .replace(/style="font-size:(\d+)px"/g, (match, size) => {
      if (size <= 12) return 'class="text-xs"';
      if (size <= 14) return 'class="text-sm"';
      if (size <= 16) return 'class="text-base"';
      if (size <= 18) return 'class="text-lg"';
      if (size <= 20) return 'class="text-xl"';
      if (size <= 24) return 'class="text-2xl"';
      if (size <= 30) return 'class="text-3xl"';
      return 'class="text-4xl"';
    })
    .replace(/style="color:#([0-9a-f]{6})"/gi, '')
    .replace(/style="background-color:#([0-9a-f]{6})"/gi, '')
    .replace(/style="font-family:[^"]*"/g, '')
    .replace(/class="ql-[^"]*"/g, '')
    .replace(/\s+/g, ' ')
    .trim();
    
  return cleaned;
};

// Helper function to get subcategories for a category
const getSubcategoriesForCategory = (categoryValue) => {
  const category = categories.find(cat => cat.value === categoryValue);
  return category?.subcategories || [];
};


const isAdminUser = () => {
  try {
    // Try multiple possible keys where user data might be stored
    const userData = localStorage.getItem("user_data") || 
                     localStorage.getItem("user") || 
                     localStorage.getItem("auth_user");
    
    if (userData) {
      const parsed = JSON.parse(userData);
      console.log('👤 User data from AdminCreatePost:', parsed);
      
      // Backend only allows users with ID 4 or 83
      const adminIds = [4, 83];
      const userId = parseInt(parsed?.id || parsed?.user_id || parsed?.userId);
      
      const isAdmin = adminIds.includes(userId);
      console.log('✅ Is admin from localStorage:', isAdmin, '(user ID:', userId, ')');
      
      return isAdmin;
    }
    
    console.log('❌ No user data found in localStorage');
    return false;
  } catch (e) {
    console.error('❌ Error checking admin status:', e);
    return false;
  }
};

const AdminCreatePost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const isEditMode = Boolean(id);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [blog, setBlog] = useState(null);
  
  // Image upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  
  // Category & Subcategory states
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategories, setSelectedSubcategories] = useState([]); // Array for multi-select
  const [availableSubcategories, setAvailableSubcategories] = useState([]);



  // Form
  const {
    control: controlForm,
    handleSubmit: handleSubmitForm,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      excerpt: "",
      content: "",
      category: "",
      subcategory: [], // Changed to array for multi-select
      featured_image: "",
      is_published: false,
    },
  });

  // Sync form changes
  const currentCategory = watch('category');

  // Fetch blog data for edit mode
  const fetchBlog = async () => {
    if (!isAdminUser()) {
      toast.error("Access denied. Admin only.");
      navigate("/blogs");
      return;
    }

    if (!token) {
      toast.error("Please login first");
      navigate("/login");
      return;
    }

    if (isEditMode && id) {
      setLoading(true);
      try {
        const response = await blogAdminService.getBlog(id, token);
        const blogData = response.data.data || response.data;
        setBlog(blogData);

        // Handle categories array - assuming API returns array like ["MainCategory", "Subcategory1", "Subcategory2"]
        const blogCategories = blogData.categories || [];
        const mainCategory = Array.isArray(blogCategories) ? blogCategories[0] || "" : blogData.category || "";
        // For multiple subcategories, take all items after the first one (main category)
        const subCategories = Array.isArray(blogCategories) ? blogCategories.slice(1) : [];
        
        setSelectedCategory(mainCategory);
        if (mainCategory) {
          const subs = getSubcategoriesForCategory(mainCategory);
          setAvailableSubcategories(subs);
          setSelectedSubcategories(subCategories); // Set array of subcategories
        }

        // Populate form
        setValue("title", blogData.title || "");
        setValue("excerpt", blogData.excerpt || "");
        setValue("content", blogData.content || "");
        setValue("category", mainCategory);
        setValue("subcategory", subCategories); // Set array of subcategories
        setValue("featured_image", blogData.featured_image || "");
        setValue("is_published", blogData.is_published ?? true);
        
        if (blogData.featured_image) {
          setImagePreview(blogData.featured_image);
        }
      } catch (error) {
        console.error("Error fetching blog:", error);
        toast.error("Failed to load blog post");
        navigate("/admin/blog");
      } finally {
        setLoading(false);
      }
    }
  };

  // Update subcategories when category changes
  useEffect(() => {
    if (currentCategory && currentCategory !== selectedCategory) {
      setSelectedCategory(currentCategory);
      const subs = getSubcategoriesForCategory(currentCategory);
      setAvailableSubcategories(subs);
      
      // Reset subcategories when category changes
      setSelectedSubcategories([]); // Clear array of subcategories
      setValue("subcategory", []);
    }
  }, [currentCategory, selectedCategory, setValue]);

useEffect(() => {
  const checkAdminAndFetchUser = async () => {
    // First check localStorage
    let isAdmin = isAdminUser();
    
    // If token exists but no user data in localStorage, try to fetch from API
    if (token && !isAdmin) {
      try {
        console.log('📡 Fetching user data from API...');
        const response = await axios.get(`${API_BASE}/user`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const user = response.data;
        console.log('📦 Fetched user from API:', user);
        
        // Store it in localStorage for future use
        localStorage.setItem('user_data', JSON.stringify(user));
        
        // Check admin status again with the fetched data
        const adminIds = [4, 83];
        isAdmin = adminIds.includes(parseInt(user.id));
        console.log('✅ Is admin after API fetch:', isAdmin);
        
      } catch (error) {
        console.error('Error fetching user:', error);
        toast.error('Failed to verify admin status');
        navigate("/login");
        return;
      }
    }
    
    // Final admin check
    if (!isAdmin) {
      console.log('🚫 Access denied - not admin');
      toast.error("Access denied. Admin only.");
      navigate("/blogs");
      return;
    }

    if (!token) {
      console.log('🔐 No token found');
      toast.error("Please login first");
      navigate("/login");
      return;
    }

    // Only fetch blog data if we're in edit mode and admin is verified
    if (isEditMode && id) {
      fetchBlog();
    }
  };

  checkAdminAndFetchUser();
}, [token, navigate, isEditMode, id, fetchBlog]);

  // Handle image upload
  const handleImageUpload = async (file) => {
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post(
        `${API_BASE}/admin/blogs/upload-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      if (response.data.success) {
        toast.success("Image uploaded successfully!");
        return response.data.url;
      } else {
        throw new Error(response.data.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to upload image";
      setUploadError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file selection
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setSelectedFile(file);
    setUploadError(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    try {
      setIsUploading(true);
      const imageUrl = await handleImageUpload(file);
      setValue('featured_image', imageUrl, { shouldValidate: true });
      toast.success("Image uploaded and ready!");
    } catch (error) {
      // Error already handled
    }
  };

  // Remove selected/uploaded image
  const removeImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setUploadProgress(0);
    setUploadError(null);
    setValue('featured_image', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Trigger file input
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePublish = async (blogId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/admin/blogs/${blogId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const onSubmit = async (data) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const cleanedContent = cleanReactQuillHTML(data.content);
      const categoryValue = data.category || selectedCategory || "";

      if (!categoryValue.trim()) {
        toast.error("Please select a category");
        return;
      }

      // Build categories array with main and subcategories (supporting multiple)
      const categoriesArray = [categoryValue];
      if (selectedSubcategories && selectedSubcategories.length > 0) {
        categoriesArray.push(...selectedSubcategories); // Add all subcategories
      }

      const blogData = {
        title: data.title || "",
        excerpt: data.excerpt || "",
        content: cleanedContent,
        categories: categoriesArray, // Send as array
        image: data.featured_image || imagePreview || null,
        is_published: false,
      };

      let response;

      if (isEditMode) {
        response = await blogAdminService.updateBlog(id, blogData, token);
      } else {
        response = await blogAdminService.createBlog(blogData, token);
      }

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || "Failed to save blog");
      }

      const blogId = isEditMode
        ? id
        : response.data.data?.id || response.data.id || response.data.blog?.id;

      if (data.is_published === true && blogId) {
        try {
          const publishResult = await handlePublish(blogId);
          if (publishResult?.notifications) {
            const { sent, failed, total_subscribers } = publishResult.notifications;
            toast.success(`✅ Blog published! ${sent} emails sent${failed > 0 ? `, ${failed} failed` : ""} (Total: ${total_subscribers})`);
          } else {
            toast.success("✅ Blog published successfully!");
          }
        } catch (publishError) {
          console.error("Publish error:", publishError);
          toast.warning("Blog saved, but email notification failed");
        }
      } else {
        toast.success(isEditMode ? "✅ Draft updated!" : "✅ Draft created!");
      }

      setTimeout(() => navigate("/admin/blog"), 1000);
    } catch (error) {
      console.error("❌ Error:", error);
      toast.error(error?.response?.data?.message || "Failed to save blog post");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen py-8 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="container px-6 mx-auto xl:px-20">
          <div className="flex items-center justify-center h-64">
            <div className="w-16 h-16 border-t-2 border-b-2 rounded-full animate-spin border-mainGradiant"></div>
          </div>
          <p className="mt-4 text-center text-gray-600">Loading Events...</p>
        </div>
      </div>
    );
  }

  return (
    <section className="py-16 blog-post">
      <div className="py-12 intro bg-mainGradiant">
        <div className="container px-6 mx-auto xl:px-20 md:px-3">
          <Button
            className="mb-6 text-white bg-white/20"
            onClick={() => navigate("/blogs")}
          >
            <Image src={arrowBack} alt="Back" className="w-4 h-4 mr-2" />
            Back to Events
          </Button>
        </div>
      </div>
         
      <div className="min-h-screen py-8 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="container px-6 mx-auto xl:px-20">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              isIconOnly
              variant="flat"
              onClick={() => navigate("/admin/blog")}
              className="rounded-full"
            >
              <FiArrowLeft />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditMode ? "Edit Event Post" : "Create New Event"}
              </h1>
              <p className="mt-1 text-gray-600">
                {isEditMode ? "Update your existing event" : "Share your thoughts with the world"}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmitForm(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Main Form */}
              <div className="space-y-6 lg:col-span-2">
                <Card className="shadow-sm">
                  <CardHeader className="pb-0">
                    <h3 className="text-lg font-semibold">Post Content</h3>
                  </CardHeader>
                  <CardBody className="pt-4 space-y-6">
                    {/* Title */}
                    <Controller
                      name="title"
                      control={controlForm}
                      rules={{
                        required: { value: true, message: "Title is required" },
                        minLength: { value: 3, message: "Title must be at least 3 characters" },
                        maxLength: { value: 200, message: "Maximum 200 characters" },
                      }}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          label="Post Title"
                          minRows={1}
                          placeholder="Enter a catchy title"
                          isInvalid={!!errors.title}
                          errorMessage={errors.title?.message}
                          variant="bordered"
                          radius="lg"
                          size="lg"
                          classNames={{
                            input: "text-lg border-none font-semibold ",
                          }}
                        />
                      )}
                    />

                    {/* Excerpt */}
                    <Controller
                      name="excerpt"
                      control={controlForm}
                      rules={{
                        required: { value: true, message: "Excerpt is required" },
                        maxLength: { value: 1000, message: "Maximum 1000 characters" },
                      }}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          label="Excerpt"
                          placeholder="Brief summary of your post (appears in blog listings)"
                          minRows={4}
                          maxLength={1000}
                          isInvalid={!!errors.excerpt}
                          errorMessage={errors.excerpt?.message}
                          variant="bordered"
                          radius="lg"
                          description={`${field.value.length}/1000 characters`}
                          classNames={{
                            input: "text-lg border-none font-semibold ",
                          }}
                        />
                      )}
                    />

                    {/* Content */}
                    <Controller
                      name="content"
                      control={controlForm}
                      rules={{ required: { value: true, message: "Content is required" } }}
                      render={({ field }) => (
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Post Content <span className="text-red-500">*</span>
                          </label>
                          <ReactQuill
                            theme="snow"
                            value={field.value || ''}
                            onChange={field.onChange}
                            modules={modules}
                            formats={formats}
                            className="rounded-lg border border-default-200 min-h-[300px] mb-12"
                          />
                          {errors.content && (
                            <p className="mt-2 text-sm text-danger">{errors.content.message}</p>
                          )}
                          <p className="mt-8 text-xs text-gray-500">
                            Full rich text editor with headers, lists, formatting, and more.
                          </p>
                        </div>
                      )}
                    />
                  </CardBody>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Publishing Settings */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-0">
                    <h3 className="text-lg font-semibold">Publishing Details</h3>
                  </CardHeader>
                  <CardBody className="pt-4 space-y-4">
                    {/* Main Category Selection */}
                    <Controller
                      name="category"
                      control={controlForm}
                      rules={{ required: { value: true, message: "Category is required" } }}
                      render={({ field }) => {
                        const handleCategoryChange = (keys) => {
                          const selected = Array.from(keys)[0];
                          setSelectedCategory(selected || "");
                          field.onChange(selected || "");
                          
                          // Update available subcategories when category changes
                          if (selected) {
                            const subs = getSubcategoriesForCategory(selected);
                            setAvailableSubcategories(subs);
                            setSelectedSubcategories([]); // Reset subcategories (array)
                            setValue("subcategory", []); // Clear form field (array)
                          } else {
                            setAvailableSubcategories([]);
                            setSelectedSubcategories([]); // Reset to empty array
                          }
                        };

                        return (
                          <Select
                            label="Main Category"
                            placeholder="Select a category"
                            selectedKeys={selectedCategory ? new Set([selectedCategory]) : new Set()}
                            onSelectionChange={handleCategoryChange}
                            isInvalid={!!errors.category}
                            errorMessage={errors.category?.message}
                            variant="bordered"
                            radius="lg"
                            renderValue={(items) => {
                              if (!items || items.length === 0) return null;
                              const selectedValue = items[0].key;
                              const categoryData = categories.find(cat => cat.value === selectedValue);
                              
                              return (
                                <div className="flex items-center gap-2">
                                  {categoryData && (
                                    <div 
                                      className="flex-shrink-0 w-3 h-3 rounded-full"
                                      style={{ backgroundColor: categoryData.hexColor }}
                                    />
                                  )}
                                  <span>{selectedValue}</span>
                                  {availableSubcategories.length > 0 && (
                                    <Chip size="sm" variant="flat" color="primary" className="ml-2">
                                      {availableSubcategories.length} subcategories
                                    </Chip>
                                  )}
                                </div>
                              );
                            }}
                          >
                            {categories.map((cat) => (
                              <SelectItem 
                                key={cat.value} 
                                value={cat.value}
                                textValue={cat.label}
                              >
                                <div className="flex items-center justify-between w-full">
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="flex-shrink-0 w-3 h-3 rounded-full"
                                      style={{ backgroundColor: cat.hexColor }}
                                    />
                                    <span>{cat.label}</span>
                                  </div>
                                  {cat.subcategories && cat.subcategories.length > 0 && (
                                    <span className="text-xs text-gray-500">
                                      {cat.subcategories.length} sub
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </Select>
                        );
                      }}
                    />

                    {/* Subcategory Selection (multiple selection) */}
                    {availableSubcategories.length > 0 && (
                      <Controller
                        name="subcategory"
                        control={controlForm}
                        render={({ field }) => (
                          <div className="space-y-2">
                            <Select
                              label="Subcategories (Optional)"
                              placeholder="Select subcategories"
                              selectionMode="multiple"
                              selectedKeys={new Set(selectedSubcategories)}
                              onSelectionChange={(keys) => {
                                const selected = Array.from(keys);
                                setSelectedSubcategories(selected);
                                field.onChange(selected);
                              }}
                              variant="bordered"
                              radius="lg"
                              classNames={{
                                trigger: "border-1 bg-gray-50",
                                value: "text-gray-700"
                              }}
                              startContent={<FiTag className="text-gray-400" />}
                            >
                              {availableSubcategories.map((sub) => (
                                <SelectItem key={sub.value} value={sub.value} textValue={sub.label}>
                                  <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-blue-300 rounded-full"></div>
                                    <span>{sub.label}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </Select>
                            <p className="text-xs text-gray-500">
                              Optional: Select multiple subcategories for better organization
                            </p>
                          </div>
                        )}
                      />
                    )}

                    {/* Display selected categories */}
                    {(selectedCategory || selectedSubcategories.length > 0) && (
                      <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                        <p className="mb-2 text-sm font-medium text-gray-700">Selected Categories:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedCategory && (
                            <div className="flex items-center gap-1 px-3 py-1 text-sm text-blue-800 bg-blue-100 rounded-full">
                              <div 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: getCategoryHexColor(selectedCategory) }}
                              />
                              <span>{selectedCategory}</span>
                            </div>
                          )}
                          {selectedSubcategories.map((sub) => (
                            <div 
                              key={sub} 
                              className="flex items-center gap-1 px-3 py-1 text-sm text-purple-800 bg-purple-100 rounded-full"
                            >
                              <div className="w-2 h-2 bg-purple-400 rounded-full" />
                              <span>{availableSubcategories.find(s => s.value === sub)?.label || sub}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const newSubcategories = selectedSubcategories.filter(s => s !== sub);
                                  setSelectedSubcategories(newSubcategories);
                                  setValue("subcategory", newSubcategories);
                                }}
                                className="ml-1 text-purple-600 hover:text-purple-800 focus:outline-none"
                              >
                                <FiX className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          {selectedSubcategories.length > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedSubcategories([]);
                                setValue("subcategory", []);
                              }}
                              className="ml-2 text-xs text-red-500 hover:text-red-700"
                            >
                              Clear All
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Featured Image Upload */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Featured Image
                      </label>

                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="image/*"
                        className="hidden border-none"
                      />

                      {imagePreview || watch('featured_image') ? (
                        <div className="relative group">
                          <Image
                            src={imagePreview || watch('featured_image')}
                            alt="Featured preview"
                            className="object-cover w-full h-48 rounded-lg"
                            removeWrapper
                          />
                          <div className="absolute inset-0 flex items-center justify-center transition-all duration-300 bg-black bg-opacity-0 rounded-lg opacity-0 group-hover:bg-opacity-40 group-hover:opacity-100">
                            <div className="flex gap-2">
                              <Button
                                isIconOnly
                                size="sm"
                                variant="flat"
                                color="warning"
                                onClick={triggerFileInput}
                                className="bg-white/80"
                              >
                                <FiEdit2 />
                              </Button>
                              <Button
                                isIconOnly
                                size="sm"
                                variant="flat"
                                color="danger"
                                onClick={removeImage}
                                className="bg-white/80"
                              >
                                <FiX />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={triggerFileInput}
                          className="p-6 text-center transition-all duration-300 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50"
                        >
                          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-full">
                            <FiUpload className="text-2xl text-blue-600" />
                          </div>
                          <p className="mb-1 text-sm text-gray-600">
                            Click to upload or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">
                            PNG, JPG, GIF up to 5MB
                          </p>
                        </div>
                      )}

                      {/* Upload Progress */}
                      {isUploading && (
                        <div className="space-y-2">
                          <Progress
                            value={uploadProgress}
                            color="primary"
                            size="sm"
                            classNames={{
                              track: "bg-gray-200",
                              indicator: "bg-gradient-to-r from-blue-500 to-purple-500"
                            }}
                          />
                          <p className="text-xs text-center text-gray-600">
                            Uploading... {uploadProgress}%
                          </p>
                        </div>
                      )}

                      {/* Upload Error */}
                      {uploadError && (
                        <div className="p-3 border border-red-200 rounded-lg bg-red-50">
                          <p className="flex items-center gap-2 text-sm text-red-700">
                            <FiXCircle className="flex-shrink-0" />
                            {uploadError}
                          </p>
                        </div>
                      )}

                      {/* URL Input */}
                      <Controller
                        name="featured_image"
                        control={controlForm}
                        render={({ field }) => (
                          <Textarea
                            {...field}
                            placeholder="Or paste image URL here..."
                            variant="bordered"
                            minRows={1}
                            radius="lg"
                            size="sm"
                            classNames={{
                              input: "border-none ",
                            }}
                            startContent={<FiImage className="text-gray-400" />}
                            endContent={
                              field.value && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    field.onChange('');
                                    setSelectedFile(null);
                                    setImagePreview(null);
                                  }}
                                  className="text-gray-400 border-none hover:text-gray-600"
                                >
                                  <FiX />
                                </button>
                              )
                            }
                          />
                        )}
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      color="primary"
                      isLoading={submitting}
                      disabled={submitting}
                      className="w-full text-white shadow-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-xl"
                      startContent={submitting ? null : <FiSave />}
                      size="lg"
                    >
                      {submitting ? "Saving..." : isEditMode ? "Update Post" : "Create Post"}
                    </Button>

                    {/* Cancel Button */}
                    <Button
                      type="button"
                      variant="flat"
                      className="w-full"
                      onClick={() => navigate("/admin/blog")}
                    >
                      Cancel
                    </Button>
                  </CardBody>
                </Card>

                {/* Tips Card */}
                <Card className="border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                  <CardBody className="p-4">
                    <h4 className="flex items-center gap-2 mb-2 font-semibold text-blue-900">
                      <FiCheckCircle className="text-blue-600" />
                      Writing Tips
                    </h4>
                    <ul className="space-y-2 text-sm text-blue-800">
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                        Use a clear, descriptive title
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                        Add high-quality featured image
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                        Break content into sections
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                        Choose relevant main category
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                        Add subcategory for better organization
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                        Write a compelling excerpt
                      </li>
                    </ul>
                  </CardBody>
                </Card>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default AdminCreatePost;